import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { wormColors } from "core/src/core/constants.js";
import { flagUrlSquare } from "../../../shared/flags";
import type { PublicPlayer } from "./types";
import { ModeIcon } from "./NextRoundReveal";
import { modeLook } from "../../../online/dynamic";
import s from "./RoyaleMoments.module.scss";

// The Royale's own moments (sketches A1 and C1, /debug/royale-ux):
//   Wall     everyone who is in, as a wall of cards under the title. It opens
//            the Royale, and comes back at the start of every stage: the cards
//            of those who just went out turn red and drop off the screen, and
//            who is left stands there.
//   CutLine  the pause between rounds: not the top scorers but the cut line,
//            the two just above it and everyone under it (and you, wherever
//            you are). The rows under the line are not coloured (the line
//            says it); green is through whatever happens next.
const colorOf = (p: PublicPlayer) => (wormColors as Record<string, string>)[p.colorId] || "#28c4d4";
const Crown = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" /></svg>);

// Where everyone sits on the walls: shuffled (not you top left every time) but
// the same on every screen, the order of a hash of each id and the Royale's
// number. Each player keeps that seat for the whole Royale (see `seats`).
function seatHash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
export function seatOrder<T extends { id: string }>(players: T[], seed: number): T[] {
  return players.slice().sort((a, b) => seatHash(a.id + ":" + seed) - seatHash(b.id + ":" + seed));
}

export function Wall({ players, seats, mine, out = [], kicker, title, sub, rule, solid, leaving, cardExtra }: {
  players: PublicPlayer[];
  // The seats in order, a player or null for one whose player went out in an
  // earlier stage (an empty place: nobody moves up). Left out, `players` in seat order.
  seats?: (PublicPlayer | null)[];
  mine: string[];
  out?: string[];        // these drop
  kicker?: string;       // "Stage 2"
  title: string;         // "ROYALE", "23 left"
  sub?: React.ReactNode; // a line under the title ("Last worm standing!")
  rule?: React.ReactNode;
  solid?: boolean;       // nothing of the game shows behind it (the opening)
  leaving?: boolean;     // fading away
  cardExtra?: (p: PublicPlayer) => React.ReactNode; // more on a player's card (the design labs)
}) {
  const list = seats || players;
  const n = list.length;
  const cols = n > 18 ? 6 : n > 10 ? 5 : n > 6 ? 4 : Math.max(2, n);
  let dropNo = 0;
  return (
    <div className={`${s.wallRoot} ${solid ? s.solid : ""} ${leaving ? s.leaving : ""}`} role="status">
      <div className={s.head}>
        {kicker ? <small>{kicker}</small> : <span className={s.crown}><Crown /></span>}
        <b>{title}</b>
        {sub ? (typeof sub === "string" ? <small className={s.sub}>{sub}</small> : sub) : null}
      </div>
      <div className={s.wall} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, maxWidth: `${cols * 9.4}em` }}>
        {list.map((p, i) => {
          if (!p) return <span key={"seat" + i} className={s.seatEmpty} style={{ "--in": `${0.9 + i * 0.035}s` } as React.CSSProperties} aria-hidden="true" />;
          const gone = out.indexOf(p.id) !== -1;
          const style = { "--c": colorOf(p), "--in": `${0.9 + i * 0.035}s`, "--drop": `${2.3 + (gone ? dropNo++ : 0) * 0.22}s` } as React.CSSProperties;
          return (
            <span key={p.id} className={`${s.card} ${mine.indexOf(p.id) !== -1 ? s.you : ""} ${gone ? s.gone : ""}`} style={style}>
              {/* filled with the player's colour, as the game's scoreboard cards */}
              <span className={s.face}><img src={flagUrlSquare(p.flag || "xx")} alt="" /><b>{p.name}</b>{cardExtra ? cardExtra(p) : null}</span>
            </span>
          );
        })}
      </div>
      {rule ? <div className={s.rule}>{rule}</div> : null}
    </div>
  );
}

// The badge of a stage's last round: over the arena while it is played, and on
// the next round's card just before it.
export const eliminations = (n: number) => (n === 1 ? "1 elimination" : `${n} eliminations`);
// What a stage is, on the walls (the opening's, each new stage's): a plate, the
// rule on one line and how to think about it under it.
// A stage's rounds as steps, then the knockout (picked in the Royale design
// lab, P7): a pill per round, the played ones gold and ticked, the next one lit;
// then the knockout card ("7" and a skull), quiet like a round not yet played
// until round 3 is next, when it turns red and pulses on round 3's beat; red
// and still once the stage is over. `done`: rounds played this stage.
const Skull = () => (<svg className={s.stepSkull} viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2a8 8 0 0 0-8 8c0 2.7 1.3 4.6 3 5.7V19a1 1 0 0 0 1 1h1.5v-2h1v2h3v-2h1v2H16a1 1 0 0 0 1-1v-3.3c1.7-1.1 3-3 3-5.7a8 8 0 0 0-8-8zm-3 9.5a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6zm6 0a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6z" /></svg>);
const Tick = () => (<svg className={s.stepTick} viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" /></svg>);
// `play` (the card between rounds): the round just played fills gold from the
// left, then the next one wakes and pulses (at the card's --fill-at and
// --blink-at), instead of all of it standing there from the start.
export function RoundSteps({ done, rounds, out, play }: { done: number; rounds: number; out: number; play?: boolean }) {
  const koLive = done === rounds - 1, koDone = done >= rounds;
  return (
    <span className={`${s.steps} ${play ? s.stepsPlay : ""}`} aria-label={`Round ${Math.min(done + 1, rounds)} of ${rounds}, then ${out} out`}>
      {Array.from({ length: rounds }, (_, i) => {
        const n = i + 1, st = n <= done ? s.stepDone : n === done + 1 ? s.stepNow : "";
        // the round just played starts as it looked while it was next ("Round 2"),
        // and turns to its tick once the gold has filled it
        if (play && n === done) return <span key={n} className={`${s.step} ${s.stepFillIn}`} aria-hidden="true"><small>Round</small><span className={s.fillSwap}><b>{n}</b><Tick /></span></span>;
        return <span key={n} className={`${s.step} ${st}`} aria-hidden="true"><small>Round</small>{n <= done ? <Tick /> : <b>{n}</b>}</span>;
      })}
      <span className={s.stepArrow} aria-hidden="true">›</span>
      {/* (on the card between rounds it stays quiet while round 3 is next, the
          Knockout band says it, and fills red only once the knockout is played) */}
      <span className={`${s.stepKo} ${koLive && !play ? s.stepKoLive : ""} ${koDone ? (play ? s.stepKoFill : s.stepKoDone) : ""}`} aria-hidden="true"><b>{out}</b><Skull /></span>
    </span>
  );
}
// The rule on the walls: the steps at the stage's start, the hint under them.
// With `first`, what the stage's first round is: its power-up mode, and (when
// the stage starts on a smaller arena than `from`) that the arena shrinks. With
// `reset`, a line first: everyone starts the stage on 0.
export function StageRule({ rounds, out, reset, first }: { rounds: number; out: number; reset?: boolean; first?: { mode?: string | null; size?: string | null; from?: string | null } }) {
  const look = first && first.mode ? modeLook(first.mode) : null;
  const shrinks = !!(first && first.size && first.from && first.from !== first.size);
  // A new stage's news, as dark tiles with a coloured bar on top (picked in
  // /debug/stage-notices, T5), right above the steps: the scores are reset
  // (gold) and, when it is so, the arena shrinks (red). One tile spans the pair.
  const news = reset || shrinks;
  return (
    <span className={s.ruleSteps}>
      {/* the steps set the width; what is above and under them takes it */}
      <span className={s.stepsBlock}>
        {news ? (
          <span className={`${s.news} ${reset && shrinks ? "" : s.newsOne}`}>
            {reset ? <span className={`${s.newsTile} ${s.newsGold}`}>Scores reset</span> : null}
            {shrinks ? <span className={`${s.newsTile} ${s.newsRed}`}>Arena shrinks</span> : null}
          </span>
        ) : null}
        <RoundSteps done={0} rounds={rounds} out={out} />
        {/* (the opening's hint; a new stage has its news instead) */}
        {news ? null : <small className={s.ruleHint}>No need to win, just don't lose!</small>}
        {look ? (
          // the first round, in the look of the next round's card between rounds
          <span className={s.firstMode} style={{ "--mc": look.color } as React.CSSProperties}>
            <span className={s.firstIcon}><ModeIcon mode={first!.mode} /></span>
            <span className={s.firstText}><b>{look.name}</b>{look.line ? <em>{look.line}</em> : null}</span>
          </span>
        ) : null}
      </span>
    </span>
  );
}
export function KnockoutBadge({ className }: { className?: string }) {
  return <span className={`${s.koTag} ${className || ""}`}>Knockout round</span>;
}

// The final's badge, where the Knockout round one sits: the same plate, larger
// and in gold, with the crown and what the final is played to.
export function FinalBadge({ wins, className }: { wins: number; className?: string }) {
  return <span className={`${s.koTag} ${s.finalTag} ${className || ""}`}><Crown /><b>Final</b><small>First to {wins}</small></span>;
}

// The stage's table, best first. Level on points, those who went out sit last
// (the server's tie-break put them there), then whoever scored more in the
// round just played (the server's tie-break at the cut, the better placing).
function rankAll(players: PublicPlayer[], score: Record<string, number>, gone: string[], before?: Record<string, number> | null) {
  const gain = (id: string) => (before ? (score[id] || 0) - (before[id] || 0) : 0);
  return players.slice().sort((a, b) => (score[b.id] || 0) - (score[a.id] || 0) || (gone.indexOf(a.id) === -1 ? 0 : 1) - (gone.indexOf(b.id) === -1 ? 0 : 1) || gain(b.id) - gain(a.id));
}
// `round`: the stage's round that just ended (1, 2, 3). `cut`: how many go out
// when the stage ends. `gone`: who went out (the stage is over), else null.
// Between rounds (card C1b in /debug/royale-card): the round's winner on top,
// the whole field in a list that scrolls (it opens on you), a tape across it at
// the line, and a bubble beside the card, level with your row, saying what you
// need when you are under it; the stage's steps at the bottom, on the next
// round. Once the stage is over: who went out, the rows around the line.
export function CutLine({ players, score, mine, cut, round, rounds, stageNo, gone, before, winner }: {
  players: PublicPlayer[]; score: Record<string, number>; mine: string[];
  cut: number; round: number; rounds: number; stageNo: number; gone: string[] | null;
  before?: Record<string, number> | null; winner?: PublicPlayer | null;
}) {
  const table = rankAll(players, score, gone || [], before);
  const n = table.length, line = Math.max(1, n - cut); // ranks 1..line stay
  const over = !!gone;
    // Through whatever the last round brings: more points ahead of the best player
  // under the line than a round can pay (a round's winner takes n - 1).
  const underBest = table[line] ? score[table[line].id] || 0 : 0;
  // At risk: everyone under the line, and whoever is level on points with the
  // best of them (so it can be more than go out). The line is drawn above them.
  let risk = line;
  if (!over && round >= 2 && table[line]) while (risk > 0 && (score[table[risk - 1].id] || 0) <= underBest) risk--;
  const mark = over ? line : risk;
  const zoneOf = (i: number): "risk" | "safe" | "" => {
    if (over) return gone!.indexOf(table[i].id) !== -1 ? "risk" : "safe";
    if (round < 2) return "";
    if (i >= risk) return "risk";
    return round === rounds - 1 && (score[table[i].id] || 0) > underBest + (n - 1) ? "safe" : "";
  };
  const isMine = (i: number) => mine.indexOf(table[i].id) !== -1;
  // everyone, in a list that scrolls (it opens on you, or on the line)
  const idx = table.map((_, i) => i);
  // Each row is a bar, as the game's own scoreboard draws them: the player's
  // points against the stage's leader.
  const max = Math.max(1, ...idx.map((i) => score[table[i].id] || 0));
  // what you need to pass the last one above the line (they may score too: "3+")
  const meAt = table.findIndex((p) => mine.indexOf(p.id) !== -1);
  const need = !over && cut > 0 && meAt >= mark ? Math.max(1, (score[table[line - 1].id] || 0) - (score[table[meAt].id] || 0) + 1) : 0;
  // the bubble beside your row: what you need, or (the stage over) how it went
  const bubble = need > 0 ? <>You need <b>{need}+</b> points to survive!</>
    : over && meAt !== -1 ? (gone!.indexOf(table[meAt].id) !== -1 ? <>You are out!</> : <>You made it!</>) : null;
  const listRef = useRef<HTMLDivElement>(null);
  const youRef = useRef<HTMLSpanElement>(null);
  const lineRef = useRef<HTMLSpanElement>(null);
  const [bubbleY, setBubbleY] = useState<number | null>(null);
  const track = () => {
    const list = listRef.current, row = youRef.current;
    if (!list || !row) return setBubbleY(null);
    const mid = row.offsetTop - list.scrollTop + row.offsetHeight / 2;
    setBubbleY(mid < 0 || mid > list.clientHeight ? null : list.offsetTop + mid);
  };
  useLayoutEffect(() => {
    const list = listRef.current, at = youRef.current || lineRef.current;
    if (list && at) list.scrollTop = at.offsetTop - (list.clientHeight - at.offsetHeight) / 2;
    track();
  }, []);
  const plus = (id: string) => (before ? (score[id] || 0) - (before[id] || 0) : 0);
  const steps = cut > 0 ? <RoundSteps done={over ? rounds : round} rounds={rounds} out={cut} play /> : <small>Round {round}/{rounds}</small>;
  // Between rounds the card plays in: the rows near you slide in one by one,
  // then the round just played fills, then the next one wakes (the next
  // round's card under this one rises in with it: NextRoundReveal .pcNextLate).
  const firstShown = Math.max(0, (meAt === -1 ? mark : meAt) - 5);
  const rowDelay = (i: number) => `${(0.1 + Math.min(10, Math.max(0, i - firstShown)) * 0.06).toFixed(3)}s`;
  // the next round is the knockout: say so, between the steps and the next round
  const koNext = !over && cut > 0 && round === rounds - 1;
  return (
    <div className={`${s.cut} ${s.cutRound} ${s.cutPlay} ${over ? s.cutOver : ""}`}>
      {winner ? (
        <div className={s.cutWin}><Crown /><span>Round {round}</span><img src={flagUrlSquare(winner.flag || "xx")} alt="" /><b>{winner.name}</b>{plus(winner.id) > 0 && <em>+{plus(winner.id)}</em>}</div>
      ) : null}
      {/* (the list's own ground: the card is drawn by its parts, so it ends where they do) */}
      <div className={s.listGround}>
      <div className={`${s.list} ${s.listScroll}`} ref={listRef} onScroll={track}>
        {idx.map((i) => {
          const p = table[i], z = zoneOf(i), gain = plus(p.id);
          return (
            <React.Fragment key={p.id}>
              {i === mark ? (
                // (one of the list's rows in its play-in: it comes in with the row under it, on either card)
                <span className={`${s.tape} ${over ? s.tapeOut : ""}`} ref={lineRef} style={{ "--rd": rowDelay(i) } as React.CSSProperties}><span>{over ? "Out below this line" : "You want to be above this line"}</span></span>
              ) : null}
              <span ref={isMine(i) ? youRef : undefined} className={`${s.row} ${i >= mark ? s.below : ""} ${z === "safe" && !over ? s.safe : ""} ${over && z === "risk" ? s.out : ""} ${isMine(i) ? s.you : ""}`} style={{ "--c": colorOf(p), "--w": `${((score[p.id] || 0) / max) * 100}%`, "--rd": rowDelay(i) } as React.CSSProperties}>
                <i className={s.fill} /><em>{i + 1}</em><img src={flagUrlSquare(p.flag || "xx")} alt="" /><b>{p.name}</b>
                {gain > 0 && <span className={s.gain}>+{gain}</span>}
                <strong>{score[p.id] || 0}</strong>
              </span>
            </React.Fragment>
          );
        })}
      </div>
      </div>
      {/* the stage's rounds: played ones ticked, the knockout red once it is next (or done) */}
      <div className={s.cutFoot}>{steps}</div>
      {koNext ? <div className={s.koClip}><div className={s.koRow}><Skull /><b>Knockout round</b><span>bottom {cut} out</span><Skull /></div></div> : null}
      {/* the stage over: who went out, where the next round usually sits */}
      {over ? (
        <div className={s.cutOutClip}>
          <div className={s.cutOutBar}>
            <span className={s.cutOutIcon}><Skull /></span>
            <span><b>{cut === 1 ? "1 is out" : `${cut} are out`}</b><small>{n - cut} go on to the next stage</small></span>
          </div>
        </div>
      ) : null}
      {bubble && bubbleY != null && <span className={`${s.bubble} ${over && gone!.indexOf(table[meAt].id) === -1 ? s.bubbleSafe : ""}`} style={{ top: bubbleY }}>{bubble}</span>}
    </div>
  );
}

// The opening: the wall alone on the screen while the server holds round 1 back
// (`ms` from `at`, when the round was announced). Its cards and title fade out
// as that time runs out, leaving the plain backdrop; the round starts then, and
// the backdrop fades away over it, so the game fades in and its own roll call
// (the players' name tags) begins. On the page itself (a portal), so it covers
// the waiting card too. Someone who arrives later never sees it.
const OPENING_OUT_MS = 600;   // the wall's content fades out, ending when the round starts
const OPENING_LIFT_MS = 900;  // then the backdrop lifts off the game
export function Opening({ players, mine, at, ms, rounds, out, seed = 0, mode }: { players: PublicPlayer[]; mine: string[]; at: number; ms: number; rounds: number; out: number; seed?: number; mode?: string | null }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const ids = [ms - OPENING_OUT_MS, ms, ms + OPENING_LIFT_MS].map((t) => window.setTimeout(() => tick((n) => n + 1), Math.max(0, at + t - Date.now()) + 20));
    return () => ids.forEach((id) => window.clearTimeout(id));
  }, [at, ms]);
  const [delays] = useState(() => { const now = Date.now(); return { lift: `${at + ms - now}ms`, out: `${at + ms - OPENING_OUT_MS - now}ms` }; });
  const t = Date.now() - at;
  if (t >= ms + OPENING_LIFT_MS) return null;
  // Both fades are animations timed against the clock (a delay worked out from
  // `at`, negative when already under way), not transitions: the game mounts
  // this afresh when the round starts, and a transition would then be skipped.
  // (worked out once per mount: a delay counts from when the element was made)
  return createPortal(
    <div className={s.opening} style={{ animationDelay: delays.lift }}>
      <div className={s.openingIn} style={{ animationDelay: delays.out }}>
        <Wall solid players={players} seats={seatOrder(players, seed)} mine={mine} title="Royale" sub="Last worm standing!" rule={<StageRule rounds={rounds} out={out} first={{ mode }} />} />
      </div>
    </div>,
    document.body,
  );
}
